import { DOCUMENT } from '@angular/common';
import {
  Directive,
  ElementRef,
  HostListener,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { SgTooltipArrow, SgTooltipPosition } from './sg-tooltip.model';

const FOCUSABLE_SELECTOR = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

function isKeyboardFocus(target: EventTarget | null): boolean {
  try {
    return target instanceof Element && target.matches(':focus-visible');
  } catch {
    return false; // :focus-visible unsupported
  }
}

@Directive({
  selector: '[sgTooltip]',
  standalone: false,
})
export class SgTooltipDirective implements OnDestroy {
  //#region Properties

  /**
   * Accepts either a string (for title) or a TemplateRef for custom content
   */
  @Input('sgTooltip') tooltipTitle?: string;
  @Input() tooltipDescription?: string;
  @Input() tooltipTemplateRef?: TemplateRef<any>;
  @Input() tooltipPosition: SgTooltipPosition = 'below';
  @Input() tooltipArrow: SgTooltipArrow = 'auto';
  @Input() tooltipClass?: string;
  /** Rendered as the tooltip's `data-test`; the tooltip lives on `document.body`, out of reach of the host's selectors. */
  @Input() tooltipDataTest?: string;
  @Input() tooltipShowDelay = 100;
  @Input() tooltipHideDelay = 100;
  @Input() tooltipAnimationDuration = 200;

  private _showOnOverflow = false;
  @Input()
  set showOnOverflow(value: BooleanInput) {
    this._showOnOverflow = coerceBooleanProperty(value);
  }

  get showOnOverflow(): boolean {
    return this._showOnOverflow;
  }

  @Input()
  set tooltipOpen(value: boolean | undefined) {
    if (value === undefined) {
      this.isTooltipControlled = false;
      return;
    }
    this.isTooltipControlled = true;

    const shouldShow = value && this.isOverflowConditionMet();
    if (shouldShow) {
      this.createTooltip(false);
    } else {
      this.removeTooltip();
    }
  }

  get tooltipOpen(): boolean {
    return this.tooltipElement !== null;
  }

  get isEmpty(): boolean {
    return !this.tooltipTitle && !this.tooltipDescription && !this.tooltipTemplateRef;
  }

  //#endregion

  //#region Private Properties
  /** Process-wide counter so each live tooltip gets a unique, stable id for aria wiring. */
  private static nextId = 0;
  /** The `id` of the currently-rendered tooltip element, mirrored onto the host's aria-describedby. */
  private tooltipElementId: string | null = null;
  private tooltipElement: HTMLElement | null = null;
  private showTimeoutId: number | null = null;
  private hideTimeoutId: number | null = null;
  private animationTimeoutId: number | null = null;
  /** The tooltip is controlled by the tooltipOpen input, and not user hover */
  private isTooltipControlled: boolean = false;
  private globalListenersActive = false;
  //#endregion

  //#region Ctor
  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private ngZone: NgZone,
    @Inject(DOCUMENT) private document: Document,
    private viewContainerRef: ViewContainerRef
  ) {}
  //#endregion

  //#region Host Listeners
  @HostListener('mouseenter')
  onMouseEnter() {
    if (this.isTooltipControlled) return;
    this.cancelHideTooltip();

    if (this.tooltipElement || this.isEmpty) return;

    if (!this.isOverflowConditionMet()) return;

    this.ngZone.runOutsideAngular(() => {
      this.showTimeoutId = window.setTimeout(() => {
        this.ngZone.run(() => {
          this.createTooltip();
        });
      }, this.tooltipShowDelay);
    });
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    if (this.isTooltipControlled) return;
    this.cancelShowTooltip();

    if (!this.tooltipElement) return;

    this.ngZone.runOutsideAngular(() => {
      this.hideTimeoutId = window.setTimeout(() => {
        this.ngZone.run(() => {
          this.removeTooltip();
        });
      }, this.tooltipHideDelay);
    });
  }

  // Keyboard focus only (:focus-visible), so a mouse click on a tooltip host doesn't pin the tooltip open.
  @HostListener('focusin', ['$event'])
  onFocusIn(event: FocusEvent) {
    if (this.isOwnFocusTarget(event.target) && isKeyboardFocus(event.target)) this.onMouseEnter();
  }

  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent) {
    if (this.isWithinHost(event.relatedTarget)) return; // focus moved inside the host: no hide/re-show flicker
    this.onMouseLeave();
  }
  //#endregion

  //#region Lifecycle Hooks
  ngOnDestroy() {
    this.cancelShowTooltip();
    this.cancelHideTooltip();
    this.cancelAnimationTimeout();
    this.destroyTooltip();
  }
  //#endregion

  //#region Private Methods
  // The host itself, or the single control it wraps (e.g. sg-button's inner <button>); not one of several children.
  private isOwnFocusTarget(target: EventTarget | null): boolean {
    const host: HTMLElement = this.el.nativeElement;
    if (target === host) return true;

    const focusables = host.querySelectorAll(FOCUSABLE_SELECTOR);

    return focusables.length === 1 && focusables[0] === target;
  }

  private isWithinHost(target: EventTarget | null): boolean {
    return target instanceof Node && (this.el.nativeElement as HTMLElement).contains(target);
  }

  private isOverflowConditionMet(): boolean {
    if (!this.showOnOverflow) return true;
    return this.el.nativeElement.scrollWidth > this.el.nativeElement.offsetWidth;
  }

  private cancelShowTooltip() {
    if (this.showTimeoutId !== null) {
      clearTimeout(this.showTimeoutId);
      this.showTimeoutId = null;
    }
  }

  private cancelHideTooltip() {
    if (this.hideTimeoutId !== null) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = null;
    }
  }

  private cancelAnimationTimeout() {
    if (this.animationTimeoutId !== null) {
      clearTimeout(this.animationTimeoutId);
      this.animationTimeoutId = null;
    }
  }

  private createTooltip(autoClose = true) {
    if (!autoClose) {
      this.cancelHideTooltip();
      if (this.tooltipElement || this.isEmpty) return;
    }

    // Tooltip container
    const tooltip = this.renderer.createElement('div');
    this.renderer.addClass(tooltip, 'sg-tooltip');
    this.renderer.addClass(tooltip, `sg-tooltip--${this.tooltipPosition}`);

    // A11y: expose the tooltip as an ARIA tooltip and associate it with the
    // host so screen readers announce it as the host's description. The
    // tooltip lives on `document.body` (top layer), so the link has to be by
    // id — `aria-describedby` resolves across the whole document. Cleared in
    // `destroyTooltip` so a stale reference never dangles once the node is gone.
    this.tooltipElementId = `sg-tooltip-${(SgTooltipDirective.nextId += 1)}`;
    this.renderer.setAttribute(tooltip, 'id', this.tooltipElementId);
    this.renderer.setAttribute(tooltip, 'role', 'tooltip');

    if (this.tooltipClass) {
      this.renderer.addClass(tooltip, this.tooltipClass);
    }

    if (this.tooltipDataTest) {
      this.renderer.setAttribute(tooltip, 'data-test', this.tooltipDataTest);
    }

    // Handle arrow class based on the arrow property
    if (this.tooltipArrow === 'none') {
      this.renderer.addClass(tooltip, 'sg-tooltip--arrow-none');
    } else if (this.tooltipArrow === 'auto') {
      this.renderer.addClass(tooltip, 'sg-tooltip--arrow-auto');
    } else {
      this.renderer.addClass(tooltip, `sg-tooltip--arrow-${this.tooltipArrow}`);
    }

    // Content container
    const container = this.renderer.createElement('div');
    this.renderer.addClass(container, 'container');

    if (this.tooltipTemplateRef) {
      this.renderer.addClass(container, 'container--custom-template');
      // Create an embedded view and move its nodes into the container
      const view = this.viewContainerRef.createEmbeddedView(this.tooltipTemplateRef);
      view.detectChanges();
      view.rootNodes.forEach((node) => {
        this.renderer.appendChild(container, node);
      });
      // Clean up the view when tooltip is destroyed
      (tooltip as any).__sgTooltipViewRef = view;
    } else {
      if (this.tooltipTitle) {
        const titleEl = this.renderer.createElement('div');
        this.renderer.addClass(titleEl, 'sg-body-small-bold');
        this.renderer.addClass(titleEl, 'title');
        this.renderer.setProperty(titleEl, 'innerText', this.tooltipTitle);
        this.renderer.appendChild(container, titleEl);
      }
      if (this.tooltipDescription) {
        const descEl = this.renderer.createElement('div');
        this.renderer.addClass(descEl, 'sg-body-small-regular');
        this.renderer.addClass(descEl, 'description');
        this.renderer.setProperty(descEl, 'innerText', this.tooltipDescription);
        this.renderer.appendChild(container, descEl);
      }
    }

    // Only create arrow if not set to 'none'
    if (this.tooltipArrow !== 'none') {
      const arrowDiv = this.renderer.createElement('div');
      this.renderer.addClass(arrowDiv, 'tooltip-arrow');
      this.renderer.appendChild(container, arrowDiv);
    }

    this.renderer.appendChild(tooltip, container);

    // Render in the browser's top layer via the Popover API. Angular CDK 21
    // sets `usePopover: true` by default, so any open dialog/menu/overlay lives
    // in the top layer — which renders above everything else in the DOM and is
    // invisible to z-index. Without this, sg-tooltip (formerly just `z-index:
    // 10000` on a body child) renders behind any open dialog. Setting
    // `popover="manual"` + showPopover() places the tooltip in the same top
    // layer; among top-layer elements, the most recently shown one wins, so
    // hovering after a dialog opens correctly stacks the tooltip above it.
    this.renderer.setAttribute(tooltip, 'popover', 'manual');
    this.renderer.appendChild(this.document.body, tooltip);
    this.tooltipElement = tooltip;

    // Point the host at the now-mounted tooltip for assistive tech.
    if (this.tooltipElementId) {
      this.renderer.setAttribute(this.el.nativeElement, 'aria-describedby', this.tooltipElementId);
    }

    // Set the tooltip to its final scale but keep it invisible for measurement
    this.renderer.setStyle(this.tooltipElement, 'visibility', 'hidden');
    this.renderer.setStyle(this.tooltipElement, 'transform', 'scale(1)');

    // showPopover may throw if the API is unavailable (older browser) or the
    // element is already showing — guard so the tooltip still renders as a
    // plain absolutely-positioned element if the popover path isn't available.
    try {
      (this.tooltipElement as HTMLElement & { showPopover?: () => void }).showPopover?.();
    } catch {
      // Fall back to legacy stacking; nothing else to do.
    }

    // Use a minimal timeout to ensure layout is complete
    setTimeout(() => {
      this.setPosition();
      // Now make it visible with the proper animation
      requestAnimationFrame(() => {
        if (this.tooltipElement) {
          // Reset transform for animation and show it
          this.renderer.removeStyle(this.tooltipElement, 'visibility');
          this.renderer.setStyle(this.tooltipElement, 'transform', 'scale(0.95)');
          this.renderer.addClass(this.tooltipElement, 'sg-tooltip--visible');
        }
      });
    }, 1);

    // Listen for scroll/resize to reposition
    this.addScrollAndResizeListeners();
  }

  private reposition = () => this.setPosition();

  private addScrollAndResizeListeners() {
    if (this.globalListenersActive) return;
    window.addEventListener('scroll', this.reposition, { capture: true, passive: true });
    window.addEventListener('resize', this.reposition, { capture: true });
    this.globalListenersActive = true;
  }

  private removeGlobalListeners() {
    if (!this.globalListenersActive) return;
    window.removeEventListener('scroll', this.reposition, true);
    window.removeEventListener('resize', this.reposition, true);
    this.globalListenersActive = false;
  }

  private removeTooltip() {
    this.cancelShowTooltip();

    if (!this.tooltipElement) return;

    // Remove the visible class from the tooltip, triggering the fade out
    this.renderer.removeClass(this.tooltipElement, 'sg-tooltip--visible');

    // Cancel any existing animation timeout
    this.cancelAnimationTimeout();

    // Set a timeout to destroy tooltip after animation completes
    this.animationTimeoutId = window.setTimeout(() => {
      this.destroyTooltip();
    }, this.tooltipAnimationDuration);
  }

  private destroyTooltip() {
    if (this.tooltipElement) {
      // If we rendered a template, destroy its view
      const viewRef = (this.tooltipElement as any).__sgTooltipViewRef;
      if (viewRef && typeof viewRef.destroy === 'function') {
        viewRef.destroy();
      }
      try {
        (this.tooltipElement as HTMLElement & { hidePopover?: () => void }).hidePopover?.();
      } catch {
        // Not in top layer (popover API unavailable or never shown) — proceed with removal.
      }
      this.renderer.removeChild(this.document.body, this.tooltipElement);
      this.tooltipElement = null;
      // Drop the host's aria link so it doesn't reference a removed node.
      this.renderer.removeAttribute(this.el.nativeElement, 'aria-describedby');
      this.tooltipElementId = null;
      this.removeGlobalListeners();
    }
  }

  private setPosition() {
    if (!this.tooltipElement) return;

    // Get measurements - tooltip should already be at scale(1) and positioned in DOM
    const hostRect = this.el.nativeElement.getBoundingClientRect();

    // Force layout calculation
    this.tooltipElement.offsetHeight;
    this.tooltipElement.offsetWidth;

    const tooltipRect = this.tooltipElement.getBoundingClientRect();

    // Ensure we have valid dimensions
    if (tooltipRect.width === 0 || tooltipRect.height === 0) {
      // If dimensions are still 0, try again after a frame
      requestAnimationFrame(() => this.setPosition());
      return;
    }

    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    let top = 0,
      left = 0;

    // Adjust spacing based on whether arrow is present
    const baseHorizontalSpacing = this.tooltipArrow === 'none' ? -3 : 4; // Closer when no arrow
    const baseVerticalSpacing = this.tooltipArrow === 'none' ? 0 : 6; // Closer when no arrow

    // Calculate the center point of the host element for better positioning
    const hostCenterX = hostRect.left + hostRect.width / 2;
    const hostCenterY = hostRect.top + hostRect.height / 2;

    switch (this.tooltipPosition) {
      case 'above':
        top = hostRect.top - tooltipRect.height - baseVerticalSpacing;
        left = hostCenterX - tooltipRect.width / 2;
        break;
      case 'below':
        top = hostRect.bottom + baseVerticalSpacing;
        left = hostCenterX - tooltipRect.width / 2;
        break;
      case 'left':
        top = hostCenterY - tooltipRect.height / 2;
        left = hostRect.left - tooltipRect.width - baseHorizontalSpacing;
        break;
      case 'right':
        top = hostCenterY - tooltipRect.height / 2;
        left = hostRect.right + baseHorizontalSpacing;
        break;
    }

    // Adjust if tooltip would go outside viewport
    if (left < 0) {
      left = 0;
    } else if (left + tooltipRect.width > windowWidth) {
      left = windowWidth - tooltipRect.width;
    }

    if (top < 0) {
      top = 0;
    } else if (top + tooltipRect.height > windowHeight) {
      top = windowHeight - tooltipRect.height;
    }

    this.renderer.setStyle(this.tooltipElement, 'top', `${top}px`);
    this.renderer.setStyle(this.tooltipElement, 'left', `${left}px`);
  }
  //#endregion
}
